import type { MerchantAction, MerchantTone } from "../components/merchant-components.js";

export interface MerchantBadgeView {
  readonly label: string;
  readonly tone: MerchantTone;
}

export interface MerchantActionStateView extends MerchantAction {
  readonly reason?: string;
}

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export function formatMerchantDate(value: string | undefined): string {
  if (value === undefined) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_FORMATTER.format(date);
}

export function formatMerchantCount(value: number | undefined): string {
  return new Intl.NumberFormat("en-US").format(value ?? 0);
}

export function formatMerchantMoneyMinor(value: number | undefined, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format((value ?? 0) / 100);
}

export function healthBadge(status: string | undefined, label?: string): MerchantBadgeView {
  if (label !== undefined) return { label, tone: toneForStatus(status) };

  return { label: labelForHealthStatus(status), tone: toneForStatus(status) };
}

function labelForHealthStatus(status: string | undefined): string {
  switch (status) {
    case "HEALTHY":
    case "FRESH":
    case "COMPLETE":
    case "READY":
      return "Healthy";
    case "DEGRADED":
    case "DELAYED":
    case "STALE":
    case "ACTION_REQUIRED":
    case "RUNNING":
      return "Needs attention";
    case "UNHEALTHY":
    case "FAILED":
    case "UNAVAILABLE":
    case "BLOCKING":
      return "Blocked";
    default:
      return "Unknown";
  }
}

function toneForStatus(status: string | undefined): MerchantTone {
  switch (status) {
    case "green":
    case "success":
    case "HEALTHY":
    case "FRESH":
    case "COMPLETE":
    case "READY":
      return "green";
    case "orange":
    case "warning":
    case "DEGRADED":
    case "DELAYED":
    case "STALE":
    case "ACTION_REQUIRED":
    case "RUNNING":
      return "orange";
    case "red":
    case "danger":
    case "UNHEALTHY":
    case "FAILED":
    case "UNAVAILABLE":
    case "BLOCKING":
      return "red";
    case "blue":
    case "info":
      return "blue";
    case "purple":
      return "purple";
    default:
      return "gray";
  }
}

export function actionState(
  label: string,
  allowed: boolean,
  reason: string,
): MerchantActionStateView {
  return allowed
    ? { label, variant: "primary" }
    : { label, variant: "soft", disabled: true, reason };
}
